import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { ConnectivityProvider } from './offline/ConnectivityContext';
import { SyncProvider } from './offline/SyncContext';
import { ToastProvider } from './design-system/components/Toast/ToastContext';
import { ConfirmProvider } from './design-system/components/ConfirmDialog/ConfirmContext';
import { httpStatus } from './lib/apiError';
import { isChromelessPath, isPublicPath } from './lib/publicPaths';
import { registerSW } from 'virtual:pwa-register';
import { updateStore } from '@/pwa/updateStore';
import { installPreloadRecovery } from '@/pwa/preloadRecovery';
import './styles/index.css';

// Configure React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors — httpStatus reads both raw axios errors
        // and the enhanced errors the api client rethrows, so a 403 fails
        // fast into the page's error state instead of spinning through
        // three doomed retries.
        const status = httpStatus(error);
        if (status !== undefined && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Service worker (prompt, 2026-08-28). A new deploy installs and WAITS; the
// UpdateBanner offers Reload and nothing reloads on its own — autoUpdate used
// to yank open pages mid-work (an invoice import lost its defaults, a PDF
// review lost its zoom). `immediate` registers right away; the hourly check
// catches long-lived sessions that never navigate.
// A tab the waiting worker does not serve can still ask for a chunk a deploy
// removed; reload once onto the current build (see pwa/preloadRecovery.ts).
installPreloadRecovery();

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    updateStore.announce(() => { void updateSW(true); });
  },
  onRegisteredSW(_swUrl, registration) {
    if (registration) {
      setInterval(() => registration.update(), 60 * 60 * 1000);
    }
  },
});

// Public pages carry no app chrome: flag the document so the rule in index.html
// hides the shared NADC navbar (a fixed 80px bar that otherwise sits on top of
// the page heading). Set BEFORE render so there is no flash. This cannot live
// as an inline <script> — CSP is script-src 'self' https://nadc1.net.
if (isPublicPath(window.location.pathname)) {
  document.documentElement.setAttribute('data-public-page', '');
}
// Signed in, but chrome-less — the TV wallboard. Same reason and same timing:
// set BEFORE render so the bar never flashes over the board.
if (isChromelessPath(window.location.pathname)) {
  document.documentElement.setAttribute('data-no-chrome', '');
}
// Since 2026-10-09 DemoOps does not load the bar and index.html has no such
// rule, so nothing reads either flag. Both stay so the bar can come back
// without re-plumbing while Michael decides whether it stays gone.

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ConnectivityProvider>
          <SyncProvider>
            <AuthProvider>
              <ToastProvider>
                <ConfirmProvider>
                  <App />
                </ConfirmProvider>
              </ToastProvider>
            </AuthProvider>
          </SyncProvider>
        </ConnectivityProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
